import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { PropsWithChildren, ReactNode } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import { UsdtCurrency } from '@flipster/utils/constants/currency';

import { competitionApiBunja } from '../../state';
import { HSeparator, VSeparator } from './misc/separator';
import { milestoneBunja } from './state';

interface Props {
  milestoneType: 'daily' | 'default';
}

export const BriefInfo = ({ milestoneType }: Props) => {
  const { currentMilestonePrizeAtom, currentMilestoneValueAtom } = useBunja(milestoneBunja);
  const { competitionStatusAtom } = useBunja(competitionApiBunja);
  const currentMilestonePrize = useAtomValue(currentMilestonePrizeAtom);
  const currentMilestoneValue = useAtomValue(currentMilestoneValueAtom);
  const competitionStatus = useAtomValue(competitionStatusAtom);

  return (
    <div>
      <HSeparator />
      <div
        className={flex({
          flexDirection: { base: 'column', tablet: 'row' },
          gap: { base: 24, tablet: 48 },
          py: { base: 24, tablet: 40 },
          px: 48,
        })}
      >
        <BriefInfoItem
          label={
            <FormattedMessage
              id={
                milestoneType === 'daily'
                  ? 'Campaign.r260123.Daily_total_prize_pool_symbol'
                  : 'Competition.r251215.Total_prize_pool_symbol'
              }
              values={{ symbol: UsdtCurrency }}
            />
          }
          content={<FormattedMessage id='Competition.r251215.Total_prize_pool_symbol_desc' />}
          mobileTitle={<FormattedMessage id='Autogen_common.r251126.s_Total_Prize_Pool' />}
        >
          <Formatted.ShortNumber
            value={currentMilestonePrize}
            css={{ color: competitionStatus === 'FINISHED' ? 'var(--key-color)' : 'content.default.level-5' }}
          />
        </BriefInfoItem>
        <VSeparator css={{ display: { tabletDown: 'none' }, h: 78 }} />
        <BriefInfoItem
          label={
            <FormattedMessage
              id={
                milestoneType === 'daily'
                  ? 'Campaign.r260123.Daily_total_trading_volume_symbol'
                  : 'Competition.r251215.Total_trading_volume_symbol'
              }
              values={{ symbol: UsdtCurrency }}
            />
          }
          content={<FormattedMessage id='Competition.r251215.Total_trading_volume_symbol_desc' />}
          mobileTitle={<FormattedMessage id='Competition.r251217.Total_trading_volume' />}
        >
          <Formatted.ShortNumber value={currentMilestoneValue} />
        </BriefInfoItem>
      </div>
      <HSeparator />
    </div>
  );
};

interface BriefInfoItemProps extends PropsWithChildren {
  label?: ReactNode;
  content?: ReactNode;
  mobileTitle?: ReactNode;
}
const BriefInfoItem = ({ label, children, content, mobileTitle }: BriefInfoItemProps) => {
  return (
    <div className={vstack({ flexBasis: 0, flexGrow: 1, gap: 4, textAlign: 'center' })}>
      {label && (
        <div
          className={hstack({
            textStyle: 'Text/M/Regular',
            color: 'content.default.level-3',
            textAlign: 'center',
          })}
        >
          <span>{label}</span>
          <Tooltip.Root>
            <Tooltip.Trigger>
              <IconButton hierarchy='quaternary' size='small' icon={<InformationCircleIcon />} />
            </Tooltip.Trigger>
            <Tooltip.Content bottomSheetTitle={mobileTitle} portal>
              {content}
            </Tooltip.Content>
          </Tooltip.Root>
        </div>
      )}
      <div
        className={css({
          textStyle: 'Display/L',
          color: 'content.default.level-5',
        })}
      >
        {children}
      </div>
    </div>
  );
};
